import { Classes, Icon } from "@blueprintjs/core";
import { ContextMenu2 } from "@blueprintjs/popover2";
import classNames from "classnames";
import React, { useState } from "react";

import { Tag } from "../../../../components";
import { useDeletePlaybookMutation, useUpdatePlaybookMutation } from "../../../../redux/slices/playbook-api";
import { Playbook } from "../../../../types/playbook/playbook";
import { GetPlaybookPlayCount } from "../../../../utils";
import DeletePlaybookDialog from "../dialogs/delete-playbook-dialog";
import { EditPlaybookDialog } from "../dialogs/edit-playbook-dialog";
import { PlaybookContextMenu } from "../playbook-context-menu/playbook-context-menu";
import styles from "./playbook-selector-item.module.scss";

function timeAgo(date: Date): string {
  const now = new Date();
  const timeDifference = now.getTime() - date.getTime();

  const minutes = Math.floor(timeDifference / (1000 * 60));
  const hours = Math.floor(timeDifference / (1000 * 60 * 60));
  const days = Math.floor(timeDifference / (1000 * 60 * 60 * 24));
  const weeks = Math.floor(timeDifference / (1000 * 60 * 60 * 24 * 7));

  if (minutes < 60) {
    return `${minutes} ${minutes === 1 ? "minute" : "minutes"} ago`;
  } else if (hours < 24) {
    return `${hours} ${hours === 1 ? "hour" : "hours"} ago`;
  } else if (days < 7) {
    return `${days} ${days === 1 ? "day" : "days"} ago`;
  } else {
    return `${weeks} ${weeks === 1 ? "week" : "weeks"} ago`;
  }
}

interface PlaybookSelectorItemProps {
  playbook: Playbook;
  selected: boolean;
  onClick: () => void;
  setSelectedPlaybook: (playbook: Playbook | undefined) => void;
  onPrintSettings: (playbook: Playbook) => void;
  showPrintSettings?: boolean;
}

export const PlaybookSelectorItem = ({
  playbook,
  selected,
  onClick,
  onPrintSettings,
  setSelectedPlaybook,
  showPrintSettings = true,
}: PlaybookSelectorItemProps) => {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);

  const [deletePlaybook] = useDeletePlaybookMutation();
  const [updatePlaybook] = useUpdatePlaybookMutation();

  const handleDeletePlaybook = () => {
    deletePlaybook(playbook.id);
    setSelectedPlaybook(undefined);
  };

  const handleEditPlaybook = async (newName: string, newTags: any[]) => {
    await updatePlaybook({ id: playbook.id, title: newName, tags: newTags });
    setSelectedPlaybook({ ...playbook, title: newName, tags: newTags });
  };

  return (
    <>
      <ContextMenu2
        key={playbook.id}
        content={
          <PlaybookContextMenu
            onDelete={() => setShowDeleteDialog(true)}
            onEdit={() => setShowEditDialog(true)}
            onPrintSettings={() => onPrintSettings && onPrintSettings(playbook)}
            showPrintSettings={showPrintSettings}
          />
        }
      >
        <div className={classNames(styles.playbookRowContainer, { [styles.active || ""]: selected })}>
          <div className={classNames(styles.playbookItem)} onClick={onClick}>
            <div className={styles.playbookItemMeta}>
              <div className={styles.playbookItemTitle}>
                <span>{playbook.title ? playbook.title : <span style={{ color: "#666" }}>Untitled</span>}</span>
                {playbook.tags && playbook.tags.length > 0 ? playbook.tags.map((tag) => <Tag tag={tag}></Tag>) : <></>}
              </div>
              {playbook.origin !== "CLASSIC" && (
                <div className={styles.playbookItemUpdated}>Updated {timeAgo(new Date(playbook.updatedTimestamp))}</div>
              )}
            </div>
            <div className={styles.playbookItemPlayCount}>
              {playbook.origin === "CLASSIC" && (
                <div className={styles.tooltip}>
                  <Icon icon="desktop" />
                  <div className={styles.tooltipText}>Created on FastDraw-Desktop</div>
                </div>
              )}
              {playbook.origin !== "CLASSIC" && <span>{GetPlaybookPlayCount(playbook)}</span>}
            </div>
          </div>
        </div>
      </ContextMenu2>
      <DeletePlaybookDialog
        show={showDeleteDialog}
        handleClose={() => setShowDeleteDialog(false)}
        playbook={playbook}
        handleDeletePlaybook={handleDeletePlaybook}
      />
      <EditPlaybookDialog
        show={showEditDialog}
        handleClose={() => setShowEditDialog(false)}
        playbook={playbook}
        handleEditPlaybook={handleEditPlaybook}
      />
    </>
  );
};

export const LoadingPlaybookSelectorItem = () => {
  return (
    <div className={styles.playbookItem}>
      <div className={styles.playbookItemMeta}>
        <div className={classNames(styles.playbookItemTitle, Classes.SKELETON)}>Playbook Title</div>
        <div className={classNames(styles.playbookItemUpdated, Classes.SKELETON)}>Updated</div>
      </div>

      <div className={classNames(styles.playbookItemPlayCount, Classes.SKELETON)}>1</div>
    </div>
  );
};
